import { useSuspenseQuery } from '@tanstack/react-query';
import first from 'lodash/first';
import { fetchCurrentCompanyUser } from '~/api/company-users';
import { ErrorCodes, ErrorResponse } from '~/client/backend';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import { useSuspenseLoggedUser } from '~/services/user';
import { CompanyQueryKeys } from '~/types/company';
import { isAxiosError } from '~/utils/helpers';

// intercepted errors:
// 1. subscription expired (403 + NoActiveSubscription)
// 2. user not authorized for this company (403 + UnauthorizedUser)
// these errors are expected and should degrade to a null company-user instead of crashing the app
const isGracefulCompanyAccessError = (error: unknown) =>
  isAxiosError<ErrorResponse>(error) &&
  error.response?.status === 403 &&
  [ErrorCodes.NoActiveSubscription, ErrorCodes.UnauthorizedUser].includes(
    first(error.response.data.errors)?.code as ErrorCodes,
  );

/**
 * Suspense hook returning the company-user of the logged user for the active company.
 * Mirrors `useActiveCompany`; consumers render inside the same Suspense boundaries.
 */
export const useActiveCompanyUser = () => {
  const companyId = useActiveCompanyId();
  const { data: user } = useSuspenseLoggedUser();

  const { data: companyUser } = useSuspenseQuery({
    queryKey: CompanyQueryKeys.companyUser(companyId, user.id),
    queryFn: async () => {
      try {
        return await fetchCurrentCompanyUser(companyId, user.id);
      } catch (error) {
        // See isGracefulCompanyAccessError: an expired subscription or an
        // unauthorized user must degrade to a null company-user so pages like
        // /expired-subscription and /unauthorised-company can render instead of
        // crashing the whole app.
        if (isGracefulCompanyAccessError(error)) return null;
        throw error;
      }
    },
    staleTime: Infinity,
  });

  return companyUser;
};
